import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import MergeDialogFields from "../../field/document/MergeDialogFields";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import {Slider} from "primereact/slider";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	ConvertPdfa,
	Functions,
	Information,
	MergeMode,
	Pdfa,
	PdfaLevel,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxDeleteDelete,
	ToolboxDeleteDeleteInterface,
	ToolboxMergeMerge
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {MergeOperation, PortalToolboxMergeMerge} from "../../../../shared/models/PortalToolboxMergeMerge";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import {Base64} from "js-base64";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import Preview from "../../../startscreen/Preview";
import {Tooltip} from "primereact/tooltip";
import Immutable from "../../../../shared/immutable/Immutable";
import ContentMode from "../../../ContentMode";
import DomHelper from "../../../../shared/helper/DomHelper";

interface MergeFile {
	id: string;
	name: string;
	acroform: boolean;
	xfa: boolean;
}

export interface MergeDialogStatus extends PortalDialogState<PortalToolboxMergeMerge> {
	files: Array<MergeFile>;
}

export class MergeDialogDefinition extends PortalDialog<PortalToolboxMergeMerge,
	PortalDialogProperties<PortalToolboxMergeMerge>, MergeDialogStatus> {
	private readonly PDFALEVELNONE: string = "";

	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			files: [],
			fieldErrors: new Map<MergeDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.mergeDocument, Functions.getMergeDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.MERGE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return selectedDocuments.length > 1 && MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	private buildMergeFiles(): Array<ReactNode> {
		let mergeItems: Array<ReactNode> = [];
		let mergeFiles: Array<MergeFile> = this.state.files;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;

		for (let file of mergeFiles.entries()) {
			let fileIndex: number = file[0];
			let mergeFile: MergeFile = file[1];
			let tooltipId: string = DomHelper.generateUUID(true);
			let classList: Array<string> = ["merge-item", "preview-" + tooltipId];

			let portalDocument: PortalDocument;
			try {
				portalDocument = documentManager.findDocument(mergeFile.id);
			} catch (e) {
				// can be ignored. This can happen while initially loading the dialog
				continue;
			}

			mergeItems.push(
				<div className={classList.join(" ")} key={mergeFile.id} data-pr-position={"right"}>
					<div className="merge-name">{mergeFile.name}</div>
					<div className="merge-functions">
						<Button className="merge-first" icon="icon icon-up" onClick={() => {
							let tempItem: MergeFile = mergeFiles[fileIndex - 1];
							mergeFiles[fileIndex - 1] = mergeFiles[fileIndex];
							mergeFiles[fileIndex] = tempItem;

							this.setState({
								files: mergeFiles
							});
						}}/>
						<Button className="merge-last" icon="icon icon-down" onClick={() => {
							let tempItem: MergeFile = mergeFiles[fileIndex + 1];
							mergeFiles[fileIndex + 1] = mergeFiles[fileIndex];
							mergeFiles[fileIndex] = tempItem;

							this.setState({
								files: mergeFiles
							});
						}}/>
					</div>
					<Tooltip target={".preview-" + tooltipId} autoHide={false} className="tooltip-preview"
							 appendTo={document.getElementById("root")}>
						<div className="tooltip-content">
							<Preview contentMode={ContentMode.VIEWER} {...this.props} controls={true}
									 portalDocument={Immutable.from(portalDocument.toJson())}/>
						</div>
					</Tooltip>
				</div>
			);
		}

		return mergeItems;
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxMergeMerge | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let mergeItems: Array<ReactNode> = this.buildMergeFiles();
		let mergeTarget: Array<ReactNode> = mergeItems.splice(0, 1);

		let pdfaLevel: string = this.PDFALEVELNONE;
		if (parameter!.archive) {
			pdfaLevel = FunctionHelper.applyValue(parameter?.pdfa?.convert?.level, ConvertPdfa.getLevelDefault());
		}

		return (
			<>
				<div className="p-col-12 p-lg-6 p-grid">
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid" hidden={
							!this.state.fields?.contains(MergeDialogFields.FILES)
						}>
							<div className="merge-list">
								<div className="merge-header">
									<Translate {...this.props} data="merge.documents.target" namespace="dialog"/>
								</div>
								<div className="target-items">
									{mergeTarget}
								</div>
								<div className="merge-header">
									<Translate {...this.props} data="merge.documents.selected" namespace="dialog"/>
								</div>
								<div className="merge-items">
									{mergeItems}
								</div>
							</div>
						</div>
					</fieldset>
				</div>

				<div className="p-col-12 p-lg-6 p-grid">
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(MergeDialogFields.OPERATION)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-operation">
								<Translate {...this.props} data="merge.mode.operation.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<Dropdown inputId="control-operation"
										  value={FunctionHelper.applyValue(
											  parameter?.operation, PortalToolboxMergeMerge.getOperationDefault()
										  )}
										  options={[
											  {
												  label: Translate.value(
													  "merge.mode.operation.end", {ns: "dialog"}
												  ),
												  value: MergeOperation.atTheEnd
											  },
											  {
												  label: Translate.value(
													  "merge.mode.operation.start", {ns: "dialog"}
												  ),
												  value: MergeOperation.atTheBeginning
											  },
											  {
												  label: Translate.value(
													  "merge.mode.operation.after", {ns: "dialog"}
												  ),
												  value: MergeOperation.afterPage
											  },
											  {
												  label: Translate.value(
													  "merge.mode.operation.before", {ns: "dialog"}
												  ),
												  value: MergeOperation.beforePage
											  },
											  {
												  label: Translate.value(
													  "merge.mode.operation.replace", {ns: "dialog"}
												  ),
												  value: MergeOperation.replace
											  }
										  ]}
										  onChange={(e) => this.onChange(
											  MergeDialogFields.OPERATION, e.value
										  )}
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>
						<div className="p-col-12 p-grid" hidden={
							parameter?.operation === MergeOperation.atTheBeginning ||
							parameter?.operation === MergeOperation.atTheEnd ||
							!this.state.fields?.contains(MergeDialogFields.PAGE)
						}>
							<label className="p-col-12 p-sm-5" htmlFor="control-page">
								<Translate {...this.props} data="merge.mode.page" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7 p-grid">
								<InputNumber inputId="control-page" step={1} showButtons={true} useGrouping={false}
											 value={FunctionHelper.applyValue(
												 parameter?.page, ToolboxMergeMerge.getPageDefault()
											 )}
											 min={ToolboxMergeMerge.getPageMin()}
											 onChange={(e) => this.onChange(
												 MergeDialogFields.PAGE, e.value
											 )}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(MergeDialogFields.RESET_METADATA)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-reset-metadata"/>
							<div className="p-col-12 p-sm-7 switch-container">
								<InputSwitch inputId="control-reset-metadata"
											 checked={FunctionHelper.applyValue(
												 parameter?.resetMetadata, ToolboxMergeMerge.getResetMetadataDefault()
											 )}
											 onChange={(e) => this.onChange(
												 MergeDialogFields.RESET_METADATA, e.value
											 )}/>
								<label className="switch-label" htmlFor="control-reset-metadata">
									<Translate {...this.props} data="merge.mode.reset_metadata.label"
											   namespace="dialog"/>
								</label>
							</div>
						</div>
					</fieldset>

					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(MergeDialogFields.PDFA_FORMAT)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-format">
								<Translate {...this.props} data="merge.pdfa.format.label" namespace="dialog"/>
								<Info {...this.props} data="merge.pdfa.format.info" options={{ns: "dialog"}}/>
							</label>
							<div className="p-col-12 p-sm-7">
								<Dropdown inputId="control-pdfa-format" value={pdfaLevel}
										  options={[
											  {
												  label: Translate.value(
													  "merge.pdfa.format.none", {ns: "dialog"}
												  ),
												  value: this.PDFALEVELNONE
											  },
											  {
												  label: Translate.value(
													  "merge.pdfa.format.1a", {ns: "dialog"}
												  ),
												  value: PdfaLevel._1a
											  },
											  {
												  label: Translate.value(
													  "merge.pdfa.format.1b", {ns: "dialog"}
												  ),
												  value: PdfaLevel._1b
											  },
											  {
												  label: Translate.value(
													  "merge.pdfa.format.2a", {ns: "dialog"}
												  ),
												  value: PdfaLevel._2a
											  },
											  {
												  label: Translate.value(
													  "merge.pdfa.format.2b", {ns: "dialog"}
												  ),
												  value: PdfaLevel._2b
											  },
											  {
												  label: Translate.value(
													  "merge.pdfa.format.2u", {ns: "dialog"}
												  ),
												  value: PdfaLevel._2u
											  },
											  {
												  label: Translate.value(
													  "merge.pdfa.format.3a", {ns: "dialog"}
												  ),
												  value: PdfaLevel._3a
											  },
											  {
												  label: Translate.value(
													  "merge.pdfa.format.3b", {ns: "dialog"}
												  ),
												  value: PdfaLevel._3b
											  },
											  {
												  label: Translate.value(
													  "merge.pdfa.format.3u", {ns: "dialog"}
												  ),
												  value: PdfaLevel._3u
											  },
										  ]}
										  onChange={(e) => this.onChange(
											  MergeDialogFields.PDFA_FORMAT, e.value
										  )}
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={
								 !parameter?.archive || !this.state.fields?.contains(MergeDialogFields.PDFA_RESOLUTION)
							 }>
							<label className="p-col-12 p-sm-5" htmlFor="control-pdfa-resolution">
								<Translate {...this.props} data="merge.pdfa.resolution" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7 slider-input">
								<InputNumber inputId="control-pdfa-resolution" step={1} suffix=" %" showButtons={true}
											 value={FunctionHelper.applyValue(
												 parameter?.pdfa?.convert?.imageQuality,
												 ConvertPdfa.getImageQualityDefault()
											 )}
											 min={ConvertPdfa.getImageQualityMin()} useGrouping={false}
											 max={ConvertPdfa.getImageQualityMax()}
											 onValueChange={(e) => this.onChange(
												 MergeDialogFields.PDFA_RESOLUTION, e.value
											 )}
								/>
								<Slider min={ConvertPdfa.getImageQualityMin()} step={1}
										max={ConvertPdfa.getImageQualityMax()}
										value={FunctionHelper.applyValue(
											parameter?.pdfa?.convert?.imageQuality, ConvertPdfa.getImageQualityDefault()
										)}
										onChange={(e) => this.onChange(
											MergeDialogFields.PDFA_RESOLUTION, e.value
										)}/>
							</div>
						</div>
					</fieldset>
				</div>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["merge-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.merge.general"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: MergeDialogFields, value: any): void {
		let parameter: PortalToolboxMergeMerge = this.state.parameter!.data;

		switch (field) {
			case MergeDialogFields.OPERATION:
				parameter.operation = value;
				break;
			case MergeDialogFields.PAGE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ToolboxMergeMerge.getPageDefault();
				}

				parameter.page = value;
				break;
			case MergeDialogFields.RESET_METADATA:
				parameter.resetMetadata = value;
				break;
			case MergeDialogFields.PDFA_FORMAT:
				if (FunctionHelper.isEmpty(value)) {
					parameter.archive = PortalToolboxMergeMerge.getArchiveDefault();
					break;
				}

				parameter.archive = true;
				parameter.pdfa!.convert!.level = value;
				break;
			case MergeDialogFields.PDFA_RESOLUTION:
				parameter.pdfa!.convert!.imageQuality = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-merge multiple"}/>
					<Translate {...this.props} data="merge.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxMergeMerge | undefined = this.state.parameter?.data;
		let portalDocuments: Array<PortalDocument> = this.props.context.documentManager.getSelection();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let documents: Array<MergeFile> = [];
		for (let portalDocument of portalDocuments) {
			documents.push({
				id: portalDocument.getSourceDocumentId()!,
				name: portalDocument.getFileName(),
				acroform: false,
				xfa: false
			})
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			files: documents
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();
		let mergeParameter: PortalToolboxMergeMerge = this.state.parameter!.data.clone();

		switch (mergeParameter.operation) {
			case MergeOperation.atTheBeginning:
				mergeParameter.mode = MergeMode.AtTheBeginning;
				break;
			case MergeOperation.atTheEnd:
				mergeParameter.mode = MergeMode.AtTheEnd;
				break;
			case MergeOperation.beforePage:
				mergeParameter.mode = MergeMode.BeforePage;
				break;
			case MergeOperation.afterPage:
			case MergeOperation.replace:
				mergeParameter.mode = MergeMode.AfterPage;
				break;
		}

		let mergeFiles: Array<MergeFile> = this.state.files;
		let targetFile: MergeFile = mergeFiles.splice(0, 1)[0];
		let targetDocument: PortalDocument | undefined;
		for (let portalDocument of selection) {
			if (targetFile.id === portalDocument.getSourceDocumentId()) {
				targetDocument = portalDocument;
				break;
			}
		}

		let tempFiles: Array<string> = [];
		for (let file of mergeFiles) {
			tempFiles.push(file.id);
		}
		mergeParameter.data!.value = Base64.toBase64(tempFiles.join(";"));

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			merge: mergeParameter.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;

		let hasError: boolean = false;
		try {
			await this.props.context.session.executeRequest({
				webServiceType: webServiceType,
				document: targetDocument,
				operation: operationData,
				settings: Settings.fromJson({
					pdfa: {
						pdfaEditProfile: this.props.pdfaEditProfile
					},
					signature: {
						signatureEditProfile: this.props.signatureEditProfile
					},
					compress: {
						compressProfile: this.props.compressProfile,
						compressObjects: {
							objectStreamSize: this.props.objectStreamSize
						}
					}
				} as SettingsInterface)
			});
		} catch (exception: any) {
			await this.onError(exception);
			return;
		}

		if (mergeParameter.operation === MergeOperation.replace && !hasError) {
			let deletePageParameter: Parameter<ToolboxDeleteDelete> =
				new Parameter<ToolboxDeleteDelete>(
					ToolboxDeleteDelete.fromJson({
						pages: mergeParameter.page!.toString()
					} as ToolboxDeleteDeleteInterface)
				);

			toolboxOperation = BaseToolbox.fromJson({
				delete: deletePageParameter.data
			});
			operationData = [toolboxOperation];

			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: targetDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				})
			} catch (exception: any) {
				hasError = true;
				await this.onError(exception);
			}
		}

		if (mergeParameter.archive && !hasError) {
			let pdfaParameter: Parameter<Pdfa> = new Parameter<Pdfa>(mergeParameter.pdfa!);
			webServiceType = WebServiceTypes.PDFA;

			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: targetDocument,
					operation: pdfaParameter.data,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				})
			} catch (exception: any) {
				hasError = true;
				await this.onError(exception);
			}
		}

		if (!hasError) {
			let message: Message = new Message("success.toolbox.merge", MessageType.SUCCESS);
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected hide() {
		this.setState({
			files: []
		});

		super.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.MERGE;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxMergeMerge> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		let portalDocuments: Array<PortalDocument> = this.props.context.documentManager.getSelection();

		let documents: Array<MergeFile> = [];
		for (let portalDocument of portalDocuments) {
			documents.push({
				id: portalDocument.getSourceDocumentId()!,
				name: portalDocument.getFileName(),
				acroform: false,
				xfa: false
			})
		}

		this.setState({
			files: documents
		});
	}
}

const MergeDialog = React.memo(MergeDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxMergeMerge>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxMergeMerge>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default MergeDialog;